import { AccordionItem, Button, colors, Icon } from '@dev-spendesk/grapes';
import { useHistory } from 'react-router-dom';

import {
  type UserTask,
  type UserTaskType,
} from 'modules/onboarding/setup-hub/types/userTask';
import { useCardsAccessQuery } from 'modules/physical-cards/hooks/useCardAccessQuery';
import { useCardAccessRestricted } from 'modules/requests/hooks/useCardAccessRestricted';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';
import { useUser } from 'src/core/modules/app/hooks/useUser';

import { getContentByTaskType } from './utils';

type Props = {
  setModalType: (type: UserTaskType | null) => void;
  task: UserTask;
  triggerMFAModal: (callback: () => void) => void;
};

export const ReadyToSpendTaskItem = ({
  setModalType,
  task,
  triggerMFAModal,
}: Props) => {
  const history = useHistory();
  const { t } = useTranslation('global');

  const company = useCompany();
  const user = useUser();

  const cardsAccessQueryState = useCardsAccessQuery(user.id);

  const cardAccessRestricted = useCardAccessRestricted({
    cardsAccess:
      cardsAccessQueryState.status === 'success'
        ? {
            isLoading: false,
            ...cardsAccessQueryState.data,
          }
        : { isLoading: true },
    company,
  });

  const onClick = async () => {
    switch (task.type) {
      case 'download_app':
      case 'learn_to_approve':
      case 'upload_receipt':
        setModalType(task.type);
        break;
      case 'expense_claim': {
        const path = routeFor(routes.REQUESTS.path, {
          company: company.id,
          type: 'all',
          id: 'new',
          newRequestType: 'reimbursement',
        });
        history.push(path, { productTourToDisplay: 'reimbursement' });
        break;
      }
      case 'single_use': {
        const openSinglePurchase = () => {
          const path = routeFor(routes.REQUESTS.path, {
            company: company.id,
            type: 'all',
            id: 'new',
            newRequestType: 'virtual',
          });
          history.push(path, { productTourToDisplay: 'single_purchase' });
        };
        if (
          cardAccessRestricted === 'mfaMissing' ||
          cardAccessRestricted === 'userPhoneMissing'
        ) {
          triggerMFAModal(openSinglePurchase);
        } else {
          openSinglePurchase();
        }
        break;
      }
      case 'subscription': {
        const openSubscription = () => {
          const path = routeFor(routes.REQUESTS.path, {
            company: company.id,
            type: 'all',
            id: 'new',
            newRequestType: 'virtual',
          });
          history.push(`${path}?card-type=subscription`, {
            productTourToDisplay: 'subscription',
          });
        };
        if (
          cardAccessRestricted === 'mfaMissing' ||
          cardAccessRestricted === 'userPhoneMissing'
        ) {
          triggerMFAModal(openSubscription);
        } else {
          openSubscription();
        }
        break;
      }
      default:
        break;
    }
  };

  const content = getContentByTaskType(task.type);
  if (!content) {
    return null;
  }
  const { title, description, cta } = content;

  return (
    <div className="border-0 border-t border-solid border-border-default py-16">
      <AccordionItem
        renderHeader={() => (
          <div className="flex items-center gap-8">
            {task.status === 'completed' ? (
              <Icon
                name="circle-check"
                size="l"
                color={colors.contentSuccessDefault}
              />
            ) : (
              <div className="h-24 w-24 rounded-[24px] border border-solid border-border-default" />
            )}
            <p className="title-m">{t(title)}</p>
          </div>
        )}
      >
        <div className="px-24 py-8">
          <div className="mb-16 text-content-secondary-bg-primary">
            {t(description)}
          </div>
          <Button onClick={onClick} variant="primaryBrand" text={t(cta)} />
        </div>
      </AccordionItem>
    </div>
  );
};
